import React, { useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { ToastBottomEnd } from "./Toast";
import {
  toastSuccessMessageWithParameterConfig,
  toastErrorMessageWithParameterConfig
} from "../../config/toastConfig";
import appConfig from "../../config/appConfig";

const ClaimerFollowUpModal = ({ onClose, claimId, onSuccess }) => {
  const { t, ready } = useTranslation();
  const closeBtnRef = useRef(null);

  const [followUpDate, setFollowUpDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    if (!followUpDate) {
      setErrors({ follow_up_date: [t("La date de relance est requise")] });
      return;
    }

    if (verifyTokenExpire()) {
      setLoading(true);

      const formData = new FormData();
      formData.append("follow_up_date", followUpDate);
      if (message) {
        formData.append("message", message);
      }

      if (selectedFiles.length > 0) {
        selectedFiles.forEach((file) => {
          formData.append("files[]", file);
        });
      }

      axios
        .post(
          `${appConfig.apiDomaine}/my/claims/${claimId}/claimer-follow-ups`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        )
        .then(({ data }) => {
          setLoading(false);
          ToastBottomEnd.fire(
            toastSuccessMessageWithParameterConfig(
              t("Relance du réclamant enregistrée avec succès")
            )
          );

          // Réinitialiser les champs du formulaire
          setMessage("");
          setSelectedFiles([]);

          // Fermer la modal Bootstrap
          if (closeBtnRef.current) {
            closeBtnRef.current.click();
          }

          // Exécuter les callbacks du parent
          setTimeout(() => {
            if (onSuccess) onSuccess(data);
            if (onClose) onClose();
          }, 100);
        })
        .catch((error) => {
          setLoading(false);
          if (error.response) {
            if (error.response.data && error.response.data.errors) {
              setErrors(error.response.data.errors);
            } else if (error.response.data && error.response.data.message) {
              ToastBottomEnd.fire(
                toastErrorMessageWithParameterConfig(error.response.data.message)
              );
            } else {
              ToastBottomEnd.fire(
                toastErrorMessageWithParameterConfig(
                  t("Une erreur est survenue lors de la relance")
                )
              );
            }
          }
        });
    }
  };

  if (!ready) return null;

  return (
    <div
      className="modal fade"
      id="claimerFollowUpModal"
      data-backdrop="static"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="claimerFollowUpModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="claimerFollowUpModalLabel">
              <i className="flaticon2-paper-plane mr-2 text-primary"></i>
              {t("Relance du réclamant (Informations manquantes)")}
            </h5>
            <button
              ref={closeBtnRef}
              onClick={() => onClose && onClose()}
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
            >
              <i aria-hidden="true" className="ki ki-close"></i>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Date de relance */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {t("Date de la relance")} <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className={`form-control ${
                    errors.follow_up_date ? "is-invalid" : ""
                  }`}
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  required
                />
                {errors.follow_up_date && (
                  <div className="invalid-feedback">
                    {errors.follow_up_date[0]}
                  </div>
                )}
              </div>

              {/* Message / Description */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {t("Message adressé au réclamant")}
                </label>
                <textarea
                  className={`form-control ${
                    errors.message ? "is-invalid" : ""
                  }`}
                  rows="4"
                  placeholder={t(
                    "Précisez les informations ou documents manquants demandés..."
                  )}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                {errors.message && (
                  <div className="invalid-feedback">{errors.message[0]}</div>
                )}
              </div>

              {/* Pièces jointes / Preuves */}
              <div className="form-group">
                <label className="font-weight-bold">
                  {t("Preuves / Pièces jointes")}
                </label>
                <div className="custom-file">
                  <input
                    type="file"
                    className="custom-file-input"
                    id="followUpFiles"
                    multiple
                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.bmp,.xls,.xlsx,.csv"
                    onChange={handleFileChange}
                  />
                  <label
                    className="custom-file-label"
                    htmlFor="followUpFiles"
                  >
                    {selectedFiles.length > 0
                      ? `${selectedFiles.length} ${t("fichier(s) sélectionné(s)")}`
                      : t("Choisir des fichiers...")}
                  </label>
                </div>
                <small className="form-text text-muted">
                  {t(
                    "Formats acceptés : PDF, DOC, DOCX, TXT, PNG, JPG, XLS, XLSX, CSV (Max: 20Mo par fichier)"
                  )}
                </small>

                {selectedFiles.length > 0 && (
                  <ul className="list-group mt-2">
                    {selectedFiles.map((f, idx) => (
                      <li
                        key={idx}
                        className="list-group-item d-flex justify-content-between align-items-center py-1 px-3 fs-6"
                      >
                        <span className="text-truncate" style={{ maxWidth: "80%" }}>
                          <i className="flaticon2-file mr-2 text-primary"></i>
                          {f.name}
                        </span>
                        <span className="badge badge-secondary badge-pill">
                          {(f.size / (1024 * 1024)).toFixed(2)} MB
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light-primary font-weight-bold"
                data-dismiss="modal"
                onClick={() => onClose && onClose()}
              >
                {t("Annuler")}
              </button>
              <button
                type="submit"
                className="btn btn-primary font-weight-bold"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {t("Enregistrement...")}
                  </>
                ) : (
                  <>
                    <i className="flaticon2-paper-plane mr-1"></i>
                    {t("Enregistrer la relance")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClaimerFollowUpModal;